/**
 * Typed API client for CellarManager.
 *
 * Auth model: Clerk session tokens.
 *   - The ClerkBridge component registers a token getter via setClerkTokenGetter().
 *   - Every request calls that getter to obtain a fresh short-lived Clerk JWT.
 *   - Clerk's SDK auto-refreshes the underlying session; no manual refresh needed.
 *   - A 401 from the API when a token was sent AND at least one previous request
 *     succeeded means the session has expired → dispatch auth:expired.
 *   - A 401 on the very first authenticated request (e.g. right after email
 *     verification when the server hasn't yet seen the new session) is silently
 *     ignored.  useAuth will retry once the session is confirmed by Clerk.
 *   - A 401 when no token was available (e.g. Clerk still initialising after an
 *     external redirect such as returning from Stripe Checkout) is also silently
 *     ignored — the caller should retry once the session is ready.
 */
import { useAccountStore } from '../stores/account.store';
const BASE_URL = '/api/v1';
let _getClerkToken = () => Promise.resolve(null);
/**
 * Tracks whether at least one authenticated request has succeeded in this
 * session.  Used to distinguish a genuine mid-session 401 (session expired →
 * log out) from a first-request 401 (race condition on new sign-in → retry).
 */
let _hasHadSuccessfulAuth = false;
/**
 * Called by ClerkBridge on mount (and whenever the Clerk session changes) to
 * wire up the session token getter. Must be called before any authed requests.
 */
export function setClerkTokenGetter(fn) {
    _getClerkToken = fn;
}
/**
 * Reset auth success tracking.  Called by useAuth.logout() so that after a
 * sign-out + sign-in cycle the first request is treated as "first auth" again
 * and won't immediately fire auth:expired on a transient 401.
 */
export function resetAuthSuccessState() {
    _hasHadSuccessfulAuth = false;
}
// ─── Error class ──────────────────────────────────────────────────────────────
export class ApiError extends Error {
    status;
    code;
    details;
    constructor(status, code, message, 
    /** Per-field validation errors from Zod (422 responses). Key = field name. */
    details) {
        super(message);
        this.status = status;
        this.code = code;
        this.details = details;
        this.name = 'ApiError';
    }
}
function signalAuthExpired() {
    useAccountStore.getState().clearAuth();
    window.dispatchEvent(new CustomEvent('auth:expired'));
}
// ─── Core request helpers ─────────────────────────────────────────────────────
async function request(path, options = {}, accountId) {
    const store = useAccountStore.getState();
    const activeAccountId = accountId ?? store.activeAccount?.id;
    const token = await _getClerkToken();
    const headers = {
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...options.headers,
    };
    if (token)
        headers['Authorization'] = `Bearer ${token}`;
    if (activeAccountId)
        headers['X-Account-Id'] = activeAccountId;
    const fullPath = activeAccountId
        ? `${BASE_URL}/accounts/${activeAccountId}${path}`
        : `${BASE_URL}${path}`;
    const res = await fetch(fullPath, { ...options, headers });
    if (res.status === 401) {
        // Only signal session expiry when:
        //   1. We actually sent a token (not a Clerk-initialising no-token request), AND
        //   2. We have had at least one prior successful auth'd response in this session.
        // Condition (2) prevents logging out on a first-request 401 that can occur
        // right after email verification or sign-in while the server is still catching
        // up with the new Clerk session (race condition on deployed servers).
        if (token && _hasHadSuccessfulAuth)
            signalAuthExpired();
        throw new ApiError(401, 'UNAUTHENTICATED', 'Session expired. Please sign in again.');
    }
    if (res.status === 204) {
        if (token)
            _hasHadSuccessfulAuth = true;
        return undefined;
    }
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new ApiError(res.status, body.code ?? 'UNKNOWN_ERROR', body.message ?? `HTTP ${res.status}`, body.details);
    }
    if (token)
        _hasHadSuccessfulAuth = true;
    return body;
}
/**
 * Authenticated request that is NOT account-scoped.
 * Sends the Bearer token but never prefixes /accounts/:id.
 * Use for /auth/me, /auth/profile, /auth/complete-profile, etc.
 */
async function authedRequest(path, options = {}) {
    const token = await _getClerkToken();
    const headers = {
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...options.headers,
    };
    if (token)
        headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${BASE_URL}${path}`, { ...options, headers });
    if (res.status === 401) {
        // Same guard as request(): only signal expiry when a token was sent AND we
        // have previously had a successful auth'd response.
        if (token && _hasHadSuccessfulAuth)
            signalAuthExpired();
        throw new ApiError(401, 'UNAUTHENTICATED', 'Session expired. Please sign in again.');
    }
    if (res.status === 204) {
        if (token)
            _hasHadSuccessfulAuth = true;
        return undefined;
    }
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new ApiError(res.status, body.code ?? 'UNKNOWN_ERROR', body.message ?? `HTTP ${res.status}`, body.details);
    }
    if (token)
        _hasHadSuccessfulAuth = true;
    return body;
}
// Non-account-scoped request (auth, etc.)
async function publicRequest(path, options = {}) {
    const headers = {
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...options.headers,
    };
    const res = await fetch(`${BASE_URL}${path}`, { ...options, headers });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new ApiError(res.status, body.code ?? 'UNKNOWN_ERROR', body.message ?? `HTTP ${res.status}`, body.details);
    }
    return body;
}
export const authApi = {
    /** Fetch the authenticated user's local profile and account list. */
    me: () => authedRequest('/auth/me'),
    /** Update display name or username (email changes go via Clerk). */
    updateProfile: (data) => authedRequest('/auth/profile', { method: 'PATCH', body: JSON.stringify(data) }),
    /**
     * Complete the post-signup onboarding step.
     * Must be called once after a new Clerk user's first sign-in to persist
     * the app-specific fields (username, DOB, country, terms acceptance).
     */
    completeProfile: (data) => authedRequest('/auth/complete-profile', { method: 'POST', body: JSON.stringify(data) }),
};
export const accountsApi = {
    list: async () => {
        const token = await _getClerkToken();
        const res = await fetch(`${BASE_URL}/accounts`, {
            headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                'Content-Type': 'application/json',
            },
        });
        const d = await res.json();
        return (d.data ?? d.accounts ?? []);
    },
    create: async (name, description) => {
        const token = await _getClerkToken();
        const res = await fetch(`${BASE_URL}/accounts`, {
            method: 'POST',
            headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ name, description }),
        });
        return res.json();
    },
    rename: (accountId, name, description) => request('', {
        method: 'PATCH',
        body: JSON.stringify({ name, description }),
    }, accountId),
    getMembers: (accountId) => request('/members', {}, accountId),
    invite: (accountId, email, role) => request('/invite', {
        method: 'POST',
        body: JSON.stringify({ email, role }),
    }, accountId),
    acceptInvitation: (token) => publicRequest(`/accounts/invitations/${token}/accept`, {
        method: 'POST',
    }),
    updateMemberRole: (accountId, userId, role) => request(`/members/${userId}/role`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
    }, accountId),
    removeMember: (accountId, userId) => request(`/members/${userId}`, { method: 'DELETE' }, accountId),
    archive: (accountId) => request('/archive', { method: 'POST' }, accountId),
    restore: (accountId) => request('/restore', { method: 'POST' }, accountId),
};
export const billingApi = {
    /**
     * Create a Stripe Checkout Session.
     * Returns the URL to redirect the browser to.
     * For Professional plan, organisationId is required.
     */
    checkout: (data) => authedRequest('/billing/checkout', { method: 'POST', body: JSON.stringify(data) }),
    /**
     * Fetch the current subscription state.
     * Omit organisationId for Personal plan subscriptions.
     */
    subscription: (organisationId) => authedRequest(`/billing/subscription${organisationId ? `?organisationId=${encodeURIComponent(organisationId)}` : ''}`),
    /**
     * Create a Stripe Billing Portal session.
     * Returns the URL to redirect the browser to.
     */
    portal: (data) => authedRequest('/billing/portal', { method: 'POST', body: JSON.stringify(data) }),
};
export const organisationsApi = {
    /**
     * Provision a new organisation during Professional plan onboarding.
     * Idempotent: returns the existing org if one with the same name already exists for this user.
     */
    create: (data) => authedRequest('/organisations', { method: 'POST', body: JSON.stringify(data) }),
    get: (orgId) => authedRequest(`/organisations/${orgId}`),
    update: (orgId, data) => authedRequest(`/organisations/${orgId}`, { method: 'PATCH', body: JSON.stringify(data) }),
    getMembers: (orgId) => authedRequest(`/organisations/${orgId}/members`),
    invite: (orgId, data) => authedRequest(`/organisations/${orgId}/members`, { method: 'POST', body: JSON.stringify(data) }),
    changeMemberRole: (orgId, userId, role) => authedRequest(`/organisations/${orgId}/members/${userId}/role`, { method: 'PATCH', body: JSON.stringify({ role }) }),
    removeMember: (orgId, userId) => authedRequest(`/organisations/${orgId}/members/${userId}`, { method: 'DELETE' }),
    transferOwnership: (orgId, data) => authedRequest(`/organisations/${orgId}/transfer-ownership`, { method: 'POST', body: JSON.stringify(data) }),
    createCellar: (orgId, data) => authedRequest(`/organisations/${orgId}/cellars`, { method: 'POST', body: JSON.stringify(data) }),
};
export const winesApi = {
    list: (params) => {
        // Normalise: accept both `q` and `search` (legacy) — backend expects `q`
        const { search, ...rest } = params ?? {};
        const merged = { ...rest, ...(search && !rest.q ? { q: search } : {}) };
        const qs = new URLSearchParams(Object.fromEntries(Object.entries(merged).filter(([, v]) => v !== undefined))).toString();
        return request(`/wines${qs ? `?${qs}` : ''}`);
    },
    get: (id) => request(`/wines/${id}`),
    create: (data) => request('/wines', {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    update: (id, data) => request(`/wines/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }),
    delete: (id) => request(`/wines/${id}`, { method: 'DELETE' }),
};
export const producersApi = {
    list: (search) => request(`/producers${search ? `?search=${encodeURIComponent(search)}` : ''}`),
    get: (id) => request(`/producers/${id}`),
    create: (data) => request('/producers', { method: 'POST', body: JSON.stringify(data) }),
};
export const regionsApi = {
    list: (country) => request(`/regions${country ? `?country=${encodeURIComponent(country)}` : ''}`),
};
export const bottlesApi = {
    list: (params) => request(`/bottles?${new URLSearchParams(Object.fromEntries(Object.entries(params ?? {}).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)]))).toString()}`),
    get: (id) => request(`/bottles/${id}`),
    getEvents: (id) => request(`/bottles/${id}/events`),
    getConditions: (id) => request(`/bottles/${id}/conditions`),
    addCondition: (id, data) => request(`/bottles/${id}/conditions`, {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    getTastings: (id) => request(`/bottles/${id}/tastings`),
    addTasting: (id, data) => request(`/bottles/${id}/tastings`, {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    open: (id, data) => request(`/bottles/${id}/open`, {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    markDrunk: (id, data) => request(`/bottles/${id}/drunk`, {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    tastingAccess: (id, data) => request(`/bottles/${id}/tasting-access`, {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    gift: (id, data) => request(`/bottles/${id}/gift`, {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    updateDrinkingWindow: (id, data) => request(`/bottles/${id}/drinking-window`, {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    move: (bottleIds, destinationLocationId, dutyStatus) => request('/bottles/move', {
        method: 'POST',
        body: JSON.stringify({
            bottleIds,
            destinationStorageLocationId: destinationLocationId,
            ...(dutyStatus ? { dutyStatus } : {}),
        }),
    }),
    splitCase: (data) => request('/bottles/split-case', {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    delete: (id) => request(`/bottles/${id}`, { method: 'DELETE' }),
    deleteCase: (unitId) => request(`/bottles/cases/${unitId}`, { method: 'DELETE' }),
};
export const packagingUnitsApi = {
    list: (params) => request(`/packaging-units?${new URLSearchParams(Object.fromEntries(Object.entries(params ?? {}).filter(([, v]) => v !== undefined))).toString()}`),
    get: (id) => request(`/packaging-units/${id}`),
    getEvents: (id) => request(`/packaging-units/${id}/events`),
    /** Link or unlink an invoice/PO document from a case. */
    linkInvoice: (unitId, invoiceMediaId) => request(`/packaging-units/${unitId}`, {
        method: 'PATCH',
        body: JSON.stringify({ invoiceMediaId }),
    }),
};
export const bottleGroupsApi = {
    list: (params) => request(`/bottles/grouped?${new URLSearchParams(Object.fromEntries(Object.entries({
        ...params,
        page: String(params?.page ?? 1),
        ...(params?.activeOnly !== undefined ? { activeOnly: String(params.activeOnly) } : {}),
        ...(params?.pageSize !== undefined ? { pageSize: String(params.pageSize) } : {}),
    }).filter(([, v]) => v !== undefined))).toString()}`),
};
export const merchantsApi = {
    list: (params) => request(`/merchants?${new URLSearchParams(Object.fromEntries(Object.entries({
        ...(params?.q ? { q: params.q } : {}),
        ...(params?.includeSystem !== undefined ? { includeSystem: String(params.includeSystem) } : {}),
        page: String(params?.page ?? 1),
    }).filter(([, v]) => v !== undefined))).toString()}`),
    get: (id) => request(`/merchants/${id}`),
    create: (data) => request('/merchants', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => request(`/merchants/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => request(`/merchants/${id}`, { method: 'DELETE' }),
    /** Search ALL bonded stores across all merchants. Returns stores with merchantName. */
    listAllBondedStores: (params) => request(`/merchants/bonded-stores?${new URLSearchParams(Object.fromEntries(Object.entries({ q: params?.q, merchantId: params?.merchantId })
        .filter(([, v]) => v !== undefined))).toString()}`),
    listBondedStores: (merchantId) => request(`/merchants/${merchantId}/bonded-stores`),
    createBondedStore: (merchantId, data) => request(`/merchants/${merchantId}/bonded-stores`, {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    updateBondedStore: (merchantId, storeId, data) => request(`/merchants/${merchantId}/bonded-stores/${storeId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
    }),
    deleteBondedStore: (merchantId, storeId) => request(`/merchants/${merchantId}/bonded-stores/${storeId}`, { method: 'DELETE' }),
};
export const acquisitionsApi = {
    list: () => request('/acquisition-lots'),
    get: (id) => request(`/acquisition-lots/${id}`),
    create: (data) => request('/acquisition-lots', {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    markArrived: (id, data) => request(`/acquisition-lots/${id}/mark-arrived`, {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    update: (id, data) => request(`/acquisition-lots/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }),
    getEvents: (id) => request(`/acquisition-lots/${id}/events`),
    delete: (id) => request(`/acquisition-lots/${id}`, { method: 'DELETE' }),
};
export const storageApi = {
    list: () => request('/storage-locations'),
    get: (id) => request(`/storage-locations/${id}`),
    getBottles: (locationId) => request(`/storage-locations/${locationId}/bottles`),
    create: (data) => request('/storage-locations', {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    update: (id, data) => request(`/storage-locations/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }),
    getDevices: (locationId) => request(`/storage-locations/${locationId}/devices`),
    registerDevice: (locationId, data) => request(`/storage-locations/${locationId}/devices`, {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    getReadings: (locationId, params) => request(`/storage-locations/${locationId}/environment/readings?${new URLSearchParams(params ?? {}).toString()}`),
    getAlerts: (locationId) => {
        const path = locationId
            ? `/storage-locations/${locationId}/environment/alerts`
            : '/storage-locations/environment/alerts';
        return request(path);
    },
    acknowledgeAlert: (locationId, alertId) => request(`/storage-locations/${locationId}/environment/alerts/${alertId}/acknowledge`, { method: 'POST' }),
    delete: (id, data) => request(`/storage-locations/${id}`, {
        method: 'DELETE',
        body: JSON.stringify(data ?? {}),
    }),
};
export const tastingsApi = {
    list: (params) => request(`/tastings?${new URLSearchParams(Object.fromEntries(Object.entries(params ?? {}).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)]))).toString()}`),
};
export const reportsApi = {
    inventorySummary: () => request('/reports/inventory-summary'),
    drinkNow: () => request('/reports/drink-now'),
    coravinExpiring: () => request('/reports/coravin-expiring'),
    drinkingHistory: (params) => request(`/reports/drinking-history?${new URLSearchParams(params ?? {}).toString()}`),
};
export const recommendationsApi = {
    suggest: (weatherLocation) => request('/recommendations/suggest', {
        method: 'POST',
        body: JSON.stringify({ weatherLocation }),
    }),
};
// ─── Dashboard ───────────────────────────────────────────────────────────────
// Composite from reports + alerts. Falls back gracefully if endpoints 404.
export const dashboardApi = {
    summary: () => request('/reports/dashboard-summary'),
};
// ─── Media ───────────────────────────────────────────────────────────────────
export const mediaApi = {
    prepareUpload: (data) => request('/media/prepare-upload', {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    confirmUpload: (mediaId, isPrimary) => request(`/media/${mediaId}/confirm`, {
        method: 'POST',
        body: JSON.stringify({ isPrimary }),
    }),
    getForEntity: (entityType, entityId) => request(`/media/entity/${entityType}/${entityId}`),
    delete: (mediaId) => request(`/media/${mediaId}`, { method: 'DELETE' }),
    /**
     * Search invoices & bonded store POs across the account.
     * Supports full-text search within extracted PDF text.
     */
    searchDocuments: (params) => {
        const qs = new URLSearchParams();
        if (params?.q)
            qs.set('q', params.q);
        if (params?.category)
            qs.set('category', params.category);
        return request(`/media/documents${qs.toString() ? `?${qs}` : ''}`);
    },
    /** Photos that reference this bottle via a photo annotation. Includes all
     *  annotations for each photo so PhotoAnnotationEditor can render overlays. */
    getAnnotatedForBottle: (bottleId) => request(`/media/bottle-annotated/${bottleId}`),
};
export const mediaAnnotationsApi = {
    get: (mediaId) => request(`/media/${mediaId}/annotations`),
    put: (mediaId, annotations) => request(`/media/${mediaId}/annotations`, {
        method: 'PUT',
        body: JSON.stringify({ annotations }),
    }),
};
export const importApi = {
    upload: async (file) => {
        const store = useAccountStore.getState();
        const accountId = store.activeAccount?.id;
        if (!accountId)
            throw new Error('No active account selected.');
        const form = new FormData();
        form.append('file', file);
        const headers = {};
        const token = await _getClerkToken();
        if (token)
            headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(`${BASE_URL}/accounts/${accountId}/import/upload`, {
            method: 'POST',
            headers,
            body: form,
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok)
            throw new ApiError(res.status, body.error ?? 'UPLOAD_FAILED', body.message ?? 'Upload failed');
        return body;
    },
    createBatch: (data) => request('/import/batches', {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    getBatch: (batchId) => request(`/import/batches/${batchId}`),
    listBatches: () => request('/import/batches'),
    matchRows: (batchId) => request(`/import/batches/${batchId}/match`, { method: 'POST' }),
    updateRows: (batchId, updates) => request(`/import/batches/${batchId}/rows`, { method: 'PATCH', body: JSON.stringify({ updates }) }),
    commit: (batchId) => request(`/import/batches/${batchId}/commit`, { method: 'POST' }),
    deleteBatch: (batchId) => request(`/import/batches/${batchId}`, { method: 'DELETE' }),
    searchWines: (q, vintage) => request('/import/wines/search', { method: 'POST', body: JSON.stringify({ q, vintage }) }),
};
export const vintagesApi = {
    listForWine: (wineId) => request(`/wines/${wineId}/vintages`),
    get: (wineId, vintageId) => request(`/wines/${wineId}/vintages/${vintageId}`),
    create: (wineId, data) => request(`/wines/${wineId}/vintages`, {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    update: (wineId, vintageId, data) => request(`/wines/${wineId}/vintages/${vintageId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }),
    delete: (wineId, vintageId) => request(`/wines/${wineId}/vintages/${vintageId}`, { method: 'DELETE' }),
    updateGrapes: (wineId, vintageId, grapes) => request(`/wines/${wineId}/vintages/${vintageId}/grapes`, {
        method: 'PUT',
        body: JSON.stringify({ grapes }),
    }),
    addReview: (wineId, vintageId, data) => request(`/wines/${wineId}/vintages/${vintageId}/reviews`, {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    updateReview: (wineId, vintageId, reviewId, data) => request(`/wines/${wineId}/vintages/${vintageId}/reviews/${reviewId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }),
    deleteReview: (wineId, vintageId, reviewId) => request(`/wines/${wineId}/vintages/${vintageId}/reviews/${reviewId}`, { method: 'DELETE' }),
};
export const criticsApi = {
    list: () => request('/critics'),
};
export const adminApi = {
    // ── Catalogue (wines / producers / merchants / bonded stores) ─────────────
    listWines: (params) => {
        const qs = new URLSearchParams();
        if (params?.q)
            qs.set('q', params.q);
        if (params?.producerId)
            qs.set('producerId', params.producerId);
        if (params?.colour)
            qs.set('colour', params.colour);
        if (params?.isVerified !== undefined)
            qs.set('isVerified', String(params.isVerified));
        if (params?.isPrivate !== undefined)
            qs.set('isPrivate', String(params.isPrivate));
        if (params?.page)
            qs.set('page', String(params.page));
        if (params?.pageSize)
            qs.set('pageSize', String(params.pageSize));
        return authedRequest(`/admin/wines${qs.toString() ? '?' + qs.toString() : ''}`);
    },
    getWine: (wineId) => authedRequest(`/admin/wines/${wineId}`),
    createWine: (data) => authedRequest('/admin/wines', { method: 'POST', body: JSON.stringify(data) }),
    updateWine: (wineId, data) => authedRequest(`/admin/wines/${wineId}`, { method: 'PATCH', body: JSON.stringify(data) }),
    updateWineGrapes: (wineId, grapes) => authedRequest(`/admin/wines/${wineId}/grapes`, { method: 'PUT', body: JSON.stringify({ grapes }) }),
    deleteWine: (wineId) => authedRequest(`/admin/wines/${wineId}`, { method: 'DELETE' }),
    listGrapeVarieties: (q) => {
        const qs = q ? `?q=${encodeURIComponent(q)}` : '';
        return authedRequest(`/admin/grape-varieties${qs}`);
    },
    listScoreScales: () => authedRequest('/admin/score-scales'),
    createScoreScale: (data) => authedRequest('/admin/score-scales', { method: 'POST', body: JSON.stringify(data) }),
    listCritics: () => authedRequest('/admin/critics'),
    createCritic: (data) => authedRequest('/admin/critics', { method: 'POST', body: JSON.stringify(data) }),
    updateCritic: (criticId, data) => authedRequest(`/admin/critics/${criticId}`, { method: 'PATCH', body: JSON.stringify(data) }),
    deleteCritic: (criticId) => authedRequest(`/admin/critics/${criticId}`, { method: 'DELETE' }),
    listProducers: (params) => {
        const qs = new URLSearchParams();
        if (params?.q)
            qs.set('q', params.q);
        if (params?.countryCode)
            qs.set('countryCode', params.countryCode);
        if (params?.page)
            qs.set('page', String(params.page));
        if (params?.pageSize)
            qs.set('pageSize', String(params.pageSize));
        return authedRequest(`/admin/producers${qs.toString() ? '?' + qs.toString() : ''}`);
    },
    createProducer: (data) => authedRequest('/admin/producers', { method: 'POST', body: JSON.stringify(data) }),
    updateProducer: (producerId, data) => authedRequest(`/admin/producers/${producerId}`, { method: 'PATCH', body: JSON.stringify(data) }),
    deleteProducer: (producerId) => authedRequest(`/admin/producers/${producerId}`, { method: 'DELETE' }),
    listMerchants: (params) => {
        const qs = new URLSearchParams();
        if (params?.q)
            qs.set('q', params.q);
        if (params?.isSystemRecord !== undefined)
            qs.set('isSystemRecord', String(params.isSystemRecord));
        if (params?.page)
            qs.set('page', String(params.page));
        if (params?.pageSize)
            qs.set('pageSize', String(params.pageSize));
        return authedRequest(`/admin/merchants${qs.toString() ? '?' + qs.toString() : ''}`);
    },
    createMerchant: (data) => authedRequest('/admin/merchants', { method: 'POST', body: JSON.stringify(data) }),
    updateMerchant: (merchantId, data) => authedRequest(`/admin/merchants/${merchantId}`, { method: 'PATCH', body: JSON.stringify(data) }),
    deleteMerchant: (merchantId) => authedRequest(`/admin/merchants/${merchantId}`, { method: 'DELETE' }),
    listBondedStores: (merchantId) => authedRequest(`/admin/merchants/${merchantId}/bonded-stores`),
    createBondedStore: (merchantId, data) => authedRequest(`/admin/merchants/${merchantId}/bonded-stores`, { method: 'POST', body: JSON.stringify(data) }),
    updateBondedStore: (merchantId, storeId, data) => authedRequest(`/admin/merchants/${merchantId}/bonded-stores/${storeId}`, { method: 'PATCH', body: JSON.stringify(data) }),
    deleteBondedStore: (merchantId, storeId) => authedRequest(`/admin/merchants/${merchantId}/bonded-stores/${storeId}`, { method: 'DELETE' }),
    // ── Operations console ────────────────────────────────────────────────────
    metrics: () => authedRequest('/admin/metrics'),
    users: (params) => {
        const qs = new URLSearchParams();
        if (params?.q)
            qs.set('q', params.q);
        if (params?.isLocked != null)
            qs.set('isLocked', String(params.isLocked));
        if (params?.isPlatformAdmin != null)
            qs.set('isPlatformAdmin', String(params.isPlatformAdmin));
        if (params?.page)
            qs.set('page', String(params.page));
        if (params?.pageSize)
            qs.set('pageSize', String(params.pageSize));
        const q = qs.toString();
        return authedRequest(`/admin/users${q ? `?${q}` : ''}`);
    },
    user: (userId) => authedRequest(`/admin/users/${userId}`),
    lockUser: (userId, reason) => authedRequest(`/admin/users/${userId}/lock`, { method: 'POST', body: JSON.stringify({ reason }) }),
    unlockUser: (userId) => authedRequest(`/admin/users/${userId}/unlock`, { method: 'POST' }),
    revokeUserSessions: (userId) => authedRequest(`/admin/users/${userId}/revoke-sessions`, { method: 'POST' }),
    grantAdmin: (userId) => authedRequest(`/admin/users/${userId}/grant-admin`, { method: 'POST' }),
    revokeAdmin: (userId) => authedRequest(`/admin/users/${userId}/revoke-admin`, { method: 'POST' }),
    updateUserNotes: (userId, adminNotes) => authedRequest(`/admin/users/${userId}/notes`, { method: 'PATCH', body: JSON.stringify({ adminNotes }) }),
    updateUserTags: (userId, adminTags) => authedRequest(`/admin/users/${userId}/tags`, { method: 'PATCH', body: JSON.stringify({ adminTags }) }),
    organisations: (params) => {
        const qs = new URLSearchParams();
        if (params?.q)
            qs.set('q', params.q);
        if (params?.isSuspended != null)
            qs.set('isSuspended', String(params.isSuspended));
        if (params?.page)
            qs.set('page', String(params.page));
        const q = qs.toString();
        return authedRequest(`/admin/organisations${q ? `?${q}` : ''}`);
    },
    organisation: (orgId) => authedRequest(`/admin/organisations/${orgId}`),
    suspendOrg: (orgId, reason) => authedRequest(`/admin/organisations/${orgId}/suspend`, { method: 'POST', body: JSON.stringify({ reason }) }),
    unsuspendOrg: (orgId) => authedRequest(`/admin/organisations/${orgId}/unsuspend`, { method: 'POST' }),
    transferOwnership: (orgId, newOwnerId) => authedRequest(`/admin/organisations/${orgId}/transfer-ownership`, { method: 'POST', body: JSON.stringify({ newOwnerId }) }),
    updateOrgNotes: (orgId, adminNotes) => authedRequest(`/admin/organisations/${orgId}/notes`, { method: 'PATCH', body: JSON.stringify({ adminNotes }) }),
    updateOrgTags: (orgId, adminTags) => authedRequest(`/admin/organisations/${orgId}/tags`, { method: 'PATCH', body: JSON.stringify({ adminTags }) }),
    invoices: (customerId) => authedRequest(`/admin/billing/customers/${customerId}/invoices`),
    cancelSubscription: (subId) => authedRequest(`/admin/billing/subscriptions/${subId}/cancel`, { method: 'POST' }),
    extendTrial: (subId, days) => authedRequest(`/admin/billing/subscriptions/${subId}/extend-trial`, { method: 'POST', body: JSON.stringify({ days }) }),
    applyCredit: (customerId, amountPence, description) => authedRequest(`/admin/billing/customers/${customerId}/credit`, { method: 'POST', body: JSON.stringify({ amountPence, description }) }),
    createRefund: (chargeId, reason, amount) => authedRequest(`/admin/billing/refund`, { method: 'POST', body: JSON.stringify({ chargeId, reason, amount }) }),
    auditLogs: (params) => {
        const qs = new URLSearchParams();
        if (params?.actorUserId)
            qs.set('actorUserId', params.actorUserId);
        if (params?.entityType)
            qs.set('entityType', params.entityType);
        if (params?.entityId)
            qs.set('entityId', params.entityId);
        if (params?.organisationId)
            qs.set('organisationId', params.organisationId);
        if (params?.action)
            qs.set('action', params.action);
        if (params?.from)
            qs.set('from', params.from);
        if (params?.to)
            qs.set('to', params.to);
        if (params?.page)
            qs.set('page', String(params.page));
        if (params?.pageSize)
            qs.set('pageSize', String(params.pageSize));
        const q = qs.toString();
        return authedRequest(`/admin/audit-logs${q ? `?${q}` : ''}`);
    },
};
export const analyticsApi = {
    drinkingBehaviour: () => request('/analytics/drinking-behaviour'),
    peakCurve: () => request('/analytics/peak-curve'),
    updateStrategy: (strategy) => request('/analytics/strategy', {
        method: 'PATCH',
        body: JSON.stringify({ strategy }),
    }),
};
export const wineListApi = {
    getConfig: () => request('/wine-list/config'),
    updateConfig: (updates) => request('/wine-list/config', {
        method: 'PATCH',
        body: JSON.stringify(updates),
    }),
    getInventory: () => request('/wine-list/inventory'),
    getFilterOptions: (params) => {
        const qs = new URLSearchParams();
        params?.regionFilter?.forEach(id => qs.append('regionFilter', id));
        params?.appellationFilter?.forEach(id => qs.append('appellationFilter', id));
        const query = qs.toString();
        return request(`/wine-list/filter-options${query ? `?${query}` : ''}`);
    },
    // Sections
    listSections: () => request('/wine-list/sections'),
    createSection: (data) => request('/wine-list/sections', {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    updateSection: (sectionId, data) => request(`/wine-list/sections/${sectionId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }),
    deleteSection: (sectionId) => request(`/wine-list/sections/${sectionId}`, { method: 'DELETE' }),
    reorderSections: (sections) => request('/wine-list/sections/reorder', {
        method: 'POST',
        body: JSON.stringify({ sections }),
    }),
    // List generation
    generate: () => request('/wine-list/generate', { method: 'POST' }),
    // Entries
    updateEntry: (entryId, data) => request(`/wine-list/entries/${entryId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }),
    deleteEntry: (entryId) => request(`/wine-list/entries/${entryId}`, { method: 'DELETE' }),
    reorderEntries: (entries) => request('/wine-list/entries/reorder', {
        method: 'POST',
        body: JSON.stringify({ entries }),
    }),
    createEntry: (data) => request('/wine-list/entries', {
        method: 'POST',
        body: JSON.stringify(data),
    }),
};
export const milestonesApi = {
    list: () => request('/milestones'),
    summary: () => request('/milestones/summary'),
    recompute: () => request('/milestones/recompute', { method: 'POST' }),
};
export const invoiceImportApi = {
    upload: async (file) => {
        const store = useAccountStore.getState();
        const token = await _getClerkToken();
        const accountId = store.activeAccount?.id;
        if (!accountId)
            throw new ApiError(400, 'NO_ACCOUNT', 'No active account selected.');
        const formData = new FormData();
        formData.append('file', file);
        const headers = {};
        if (token)
            headers['Authorization'] = `Bearer ${token}`;
        if (accountId)
            headers['X-Account-Id'] = accountId;
        const res = await fetch(`${BASE_URL}/accounts/${accountId}/invoice-import/upload`, {
            method: 'POST',
            body: formData,
            headers,
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok)
            throw new ApiError(res.status, body.code ?? 'UNKNOWN_ERROR', body.message ?? `HTTP ${res.status}`);
        return body;
    },
    listBatches: () => request('/invoice-import/batches'),
    getBatch: (batchId) => request(`/invoice-import/batches/${batchId}`),
    updateRows: (batchId, updates) => request(`/invoice-import/batches/${batchId}/rows`, {
        method: 'PATCH',
        body: JSON.stringify({ updates }),
    }),
    commit: (batchId) => request(`/invoice-import/batches/${batchId}/commit`, { method: 'POST' }),
    patchBatch: (batchId, data) => request(`/invoice-import/batches/${batchId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }),
    deleteBatch: (batchId) => request(`/invoice-import/batches/${batchId}`, { method: 'DELETE' }),
    searchWines: (q) => request('/invoice-import/wines/search', {
        method: 'POST',
        body: JSON.stringify({ q }),
    }),
};
